// ไฟล์: frontend/src/pages/admin/AdminCategoriesPage.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AdminLayout from "../../components/layout/AdminLayout";
import "./AdminPages.css";
import AdminSidebar from "../../components/layout/AdminSidebar";

const AdminCategoriesPage = ({ user }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "folder",
  });
  const [errors, setErrors] = useState({});
  const modalRef = useRef(null);
  const tableRef = useRef(null);

  // ดึงข้อมูลหมวดหมู่ทั้งหมด
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/categories");
        setCategories(response.data.categories);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // จัดการการปิดโมดัลเมื่อคลิกนอกพื้นที่
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModalOpen(false);
      }
    };

    if (modalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalOpen]);

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // ล้างข้อผิดพลาดเมื่อผู้ใช้แก้ไขข้อมูล
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  // ตรวจสอบความถูกต้องของข้อมูล
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "กรุณาระบุชื่อหมวดหมู่";
    }

    if (!formData.icon.trim()) {
      newErrors.icon = "กรุณาระบุไอคอน";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // เปิดโมดัลเพิ่มหมวดหมู่ใหม่
  const openAddModal = () => {
    setEditMode(false);
    setFormData({
      name: "",
      description: "",
      icon: "folder",
    });
    setErrors({});
    setModalOpen(true);
  };

  // เปิดโมดัลแก้ไขหมวดหมู่
  const openEditModal = (category) => {
    setEditMode(true);
    setCurrentCategory(category);
    setFormData({
      id: category.id,
      name: category.name,
      description: category.description || "",
      icon: category.icon || "folder",
    });
    setErrors({});
    setModalOpen(true);
  };

  // บันทึกข้อมูลหมวดหมู่
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (editMode) {
        // อัพเดทหมวดหมู่
        await axios.put("/categories", formData);

        // อัพเดทรายการหมวดหมู่ในสถานะ
        setCategories(
          categories.map((cat) =>
            cat.id === formData.id ? { ...formData } : cat
          )
        );

        Swal.fire({
          icon: "success",
          title: "สำเร็จ!",
          text: "อัพเดทหมวดหมู่เรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });
      } else {
        // สร้างหมวดหมู่ใหม่
        const response = await axios.post("/categories", formData);

        // เพิ่มหมวดหมู่ใหม่ในสถานะ
        setCategories([...categories, response.data.category]);

        Swal.fire({
          icon: "success",
          title: "สำเร็จ!",
          text: "เพิ่มหมวดหมู่ใหม่เรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });
      }

      // ปิดโมดัล
      setModalOpen(false);
    } catch (error) {
      console.error("Error saving category:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด!",
        text: "ไม่สามารถบันทึกข้อมูลได้ โปรดลองอีกครั้ง",
        confirmButtonText: "ตกลง",
      });
    }
  };

  // ลบหมวดหมู่
  const handleDelete = async (id) => {
    // ยืนยันการลบ
    const result = await Swal.fire({
      icon: "warning",
      title: "ยืนยันการลบ",
      text: "คุณแน่ใจหรือว่าต้องการลบหมวดหมู่นี้? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้",
      showCancelButton: true,
      confirmButtonText: "ใช่, ลบเลย",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/categories/${id}`);

        // ลบหมวดหมู่ออกจากสถานะ
        setCategories(categories.filter((cat) => cat.id !== id));

        Swal.fire({
          icon: "success",
          title: "ลบแล้ว!",
          text: "ลบหมวดหมู่เรียบร้อยแล้ว",
          confirmButtonText: "ตกลง",
        });
      } catch (error) {
        console.error("Error deleting category:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด!",
          text: "ไม่สามารถลบหมวดหมู่ได้ โปรดลองอีกครั้ง",
          confirmButtonText: "ตกลง",
        });
      }
    }
  };

  return (
    <div className="admin-page">
        <AdminSidebar user={user} />
      <div className="admin-header">
        <h1 className="admin-title">
          <i className="fas fa-folder"></i> จัดการหมวดหมู่
        </h1>
        <button className="btn btn-primary" onClick={openAddModal}>
          <i className="fas fa-plus"></i> เพิ่มหมวดหมู่
        </button>
      </div>

      <div className="admin-card">
        <div className="admin-card-body">
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">กำลังโหลด...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover" ref={tableRef}>
                <thead className="thead-dark">
                  <tr>
                    <th width="5%">ID</th>
                    <th width="10%">ไอคอน</th>
                    <th width="20%">ชื่อหมวดหมู่</th>
                    <th width="45%">คำอธิบาย</th>
                    <th width="20%">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td>{category.id}</td>
                      <td>
                        <i
                          className={`fas fa-${
                            category.icon || "folder"
                          } fa-lg`}
                        ></i>
                      </td>
                      <td>{category.name}</td>
                      <td>{category.description || "-"}</td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => openEditModal(category)}
                          >
                            <i className="fas fa-edit"></i> แก้ไข
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(category.id)}
                          >
                            <i className="fas fa-trash"></i> ลบ
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {categories.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center">
                        ไม่พบข้อมูลหมวดหมู่
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal เพิ่ม/แก้ไขหมวดหมู่ */}
      {modalOpen && (
        <div className="custom-modal">
          <div
            className="modal-backdrop"
            onClick={() => setModalOpen(false)}
          ></div>
          <div className="modal-content" ref={modalRef}>
            <div className="modal-header">
              <h5 className="modal-title">
                {editMode ? "แก้ไขหมวดหมู่" : "เพิ่มหมวดหมู่ใหม่"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setModalOpen(false)}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    ชื่อหมวดหมู่ <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    คำอธิบาย
                  </label>
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label htmlFor="icon" className="form-label">
                    ไอคอน <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className={`fas fa-${formData.icon || "folder"}`}></i>
                    </span>
                    <input
                      type="text"
                      className={`form-control ${
                        errors.icon ? "is-invalid" : ""
                      }`}
                      id="icon"
                      name="icon"
                      value={formData.icon}
                      onChange={handleChange}
                      placeholder="folder, book, file, etc."
                    />
                    {errors.icon && (
                      <div className="invalid-feedback">{errors.icon}</div>
                    )}
                  </div>
                  <small className="text-muted">
                    ใช้ชื่อไอคอนจาก{" "}
                    <a
                      href="https://fontawesome.com/icons"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Font Awesome
                    </a>{" "}
                    เช่น folder, book, file
                  </small>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setModalOpen(false)}
                  >
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editMode ? "บันทึกการแก้ไข" : "เพิ่มหมวดหมู่"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategoriesPage;
